/**
 * AI credits — the team's balance as the CMS ledger reports it.
 *
 * Read-only. Credits are spent server-side: the llm-gateway meters each
 * response and settles whole credits against `POST /credits/consume` for the
 * team named by the `x-trace-team-id` header (see gateway.ts). The editor's
 * job is to show what that costs, so this module only ever GETs.
 *
 * The credits API landed on the CMS after this editor shipped, so every
 * failure mode collapses to `unavailable` and the UI hides itself:
 * an environment whose CMS predates credits (404), a signed-out or
 * non-member user (401/403), and an unreachable CMS all look the same to a
 * viewer who just wants to author a scene.
 *
 * Ledger design: TraceBackend PRs #743/#750.
 */

import {
    API_BASE_URL,
    ensureFreshToken,
    getCatalogSelectedTeamId,
    onCatalogSelectedTeamIdChange,
    onCatalogTokenChange,
} from '../catalog-auth';

export interface MonthlyCredits {
    /** Credits included in the current billing month. */
    allotment: number;
    /** Credits spent so far this month. */
    used: number;
    /** ISO timestamp the monthly window rolls over, if known. */
    resetsAt: string | null;
}

export interface TeamCredits {
    /** Spendable credits right now, across all unexpired grants. */
    balance: number;
    monthly: MonthlyCredits;
}

export type CreditsState =
    /** No team selected, or signed out — there is nothing to report. */
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; credits: TeamCredits; teamId: string }
    /** This CMS has no credits API, or it refused us. Show nothing. */
    | { status: 'unavailable' };

/** Serve a cached balance for this long before refetching. */
const TTL_MS = 30_000;

let state: CreditsState = { status: 'idle' };
/** When the last settled answer arrived, and for which team. */
let fetchedAt = 0;
let fetchedTeamId: string | null = null;
/**
 * In-flight lookups, keyed by team. Not a single slot: a team switch fires a
 * forced refresh while the previous team's request may still be running, and a
 * shared slot would hand the new team the old team's promise — its request
 * would never be made, and its balance would never arrive.
 */
const inFlight = new Map<string, Promise<void>>();
const listeners = new Set<(state: CreditsState) => void>();

export function getCreditsState(): CreditsState {
    return state;
}

export function onCreditsChange(listener: (state: CreditsState) => void): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

function setState(next: CreditsState): void {
    state = next;
    for (const listener of listeners) listener(next);
}

/**
 * Whether a lookup's result is still worth showing. A team switch during a
 * request must not leave the previous team's balance on screen labelled as the
 * new team's.
 */
function isCurrent(teamId: string): boolean {
    return getCatalogSelectedTeamId() === teamId;
}

/**
 * Mark the moment a lookup settled, whatever its outcome, for the TTL.
 *
 * Guarded like the state writes: a request whose team was switched away from mid-flight must not
 * take ownership of the cache slot. It would leave `fetchedTeamId` naming a team that is no longer
 * selected, so the freshness check could never pass again and the TTL would be silently dead for
 * the rest of the session — every mount refetching.
 */
function settle(teamId: string): void {
    if (!isCurrent(teamId)) return;
    fetchedAt = Date.now();
    fetchedTeamId = teamId;
}

/** Narrow the CMS payload; a shape we don't recognize is not a balance. */
function parseCredits(body: unknown): TeamCredits | null {
    if (typeof body !== 'object' || body === null) return null;
    const dto = body as { balance?: unknown; monthly?: unknown };
    if (typeof dto.balance !== 'number') return null;
    const monthly = (typeof dto.monthly === 'object' && dto.monthly !== null ? dto.monthly : {}) as {
        allotment?: unknown; used?: unknown; resetsAt?: unknown;
    };
    return {
        balance: dto.balance,
        monthly: {
            allotment: typeof monthly.allotment === 'number' ? monthly.allotment : 0,
            used: typeof monthly.used === 'number' ? monthly.used : 0,
            resetsAt: typeof monthly.resetsAt === 'string' ? monthly.resetsAt : null,
        },
    };
}

/**
 * Refresh the balance for the selected team. Cheap to call — a fresh result
 * inside the TTL is reused, and concurrent calls share one request.
 *
 * @param force Refetch even within the TTL. Use after spending credits.
 */
export function refreshCredits(force = false): Promise<void> {
    const teamId = getCatalogSelectedTeamId();
    if (!teamId || !API_BASE_URL) {
        setState({ status: 'idle' });
        return Promise.resolve();
    }
    // "Unavailable" is cached like a balance is: an environment whose CMS
    // predates credits would otherwise 404 on every panel mount, forever.
    const fresh = fetchedAt > 0
        && fetchedTeamId === teamId
        && Date.now() - fetchedAt < TTL_MS;
    // A forced refresh follows spending, which can only move a balance that
    // exists. Once the verdict is "this CMS has no credits API", forcing would
    // re-404 after every single turn (prod does exactly this) — so let the TTL
    // govern instead, which still re-probes if one appears.
    const forcing = force && state.status !== 'unavailable';
    if (fresh && !forcing) return Promise.resolve();

    const existing = inFlight.get(teamId);
    if (existing) return existing;

    const task = (async () => {
        try {
            const token = await ensureFreshToken();
            if (!token) {
                if (isCurrent(teamId)) setState({ status: 'idle' });
                return;
            }
            // Keep any balance already on screen while refetching; only a
            // first load has nothing better to show than a spinner.
            if (!isCurrent(teamId)) return;
            if (state.status !== 'ready' || state.teamId !== teamId) {
                setState({ status: 'loading' });
            }
            const base = API_BASE_URL.replace(/\/+$/, '');
            const res = await fetch(`${base}/teams/${teamId}/credits`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                // 404 = CMS predates credits; 401/403 = not our team to see.
                if (res.status !== 404 && res.status !== 401 && res.status !== 403) {
                    console.warn(`[AI] credits lookup returned ${res.status}`);
                }
                settle(teamId);
                if (isCurrent(teamId)) setState({ status: 'unavailable' });
                return;
            }
            const credits = parseCredits(await res.json());
            if (!credits) {
                console.warn('[AI] credits response had an unexpected shape');
                settle(teamId);
                if (isCurrent(teamId)) setState({ status: 'unavailable' });
                return;
            }
            settle(teamId);
            if (isCurrent(teamId)) setState({ status: 'ready', credits, teamId });
        } catch (err) {
            console.warn('[AI] credits lookup failed:', err);
            settle(teamId);
            if (isCurrent(teamId)) setState({ status: 'unavailable' });
        } finally {
            inFlight.delete(teamId);
        }
    })();
    inFlight.set(teamId, task);
    return task;
}

/**
 * Credits available to spend. The monthly allotment is already folded into
 * the ledger balance by the CMS, so the balance is the whole story — but a
 * negative balance means the team is overdrafting metered AI usage, and
 * "-12 credits available" reads as a bug rather than a debt.
 */
export function availableCredits(credits: TeamCredits): number {
    return Math.max(0, credits.balance);
}

/** Whether the team has run down its credits (and is now on overdraft). */
export function isOverdrawn(credits: TeamCredits): boolean {
    return credits.balance <= 0;
}

/** e.g. "1,240" — credit counts are integers by ledger contract. */
export function formatCredits(value: number): string {
    return Math.round(value).toLocaleString();
}

// A team switch re-scopes the whole editor, and signing in or out changes
// whether there is a balance to read at all. Both invalidate what we hold.
onCatalogSelectedTeamIdChange(() => {
    fetchedAt = 0;
    fetchedTeamId = null;
    setState({ status: 'idle' });
    void refreshCredits(true);
});
onCatalogTokenChange(() => {
    fetchedAt = 0;
    fetchedTeamId = null;
    void refreshCredits(true);
});
